import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { EnergyCapacity } from '../fields/EnergyCapacity';
import { ChargeCapacity } from '../fields/ChargeCapacity';
import { Resistance } from '../fields/Resistance';

import { AbstractPort, Port } from '../fields/AbstractPort';
import { ComponentDefinition } from '../../types/ComponentDefinition';
import { BatteryTechnologies } from '../fields/BatteryTechnologies';
import { ParallelableCapacity } from '../fields/ParallelableCapacity';

import { environmentalWithCoolingMethod } from '../component-sections/environmental';

const port = AbstractPort.extend({
    DC: Port.DC.default({}),
    equivalentSeriesResistance: Port.equivalentSeriesResistance.validator,
    powerFlowDirection: Port.powerFlowDirection.literals.bidirectional,
    resistance: Resistance.validator,
}).default({});

const BatteryBase = z.object({
    type: z.literal('battery').default('battery'),
    electrical: z
        .object({
            ports: z.tuple([port]).default([undefined]),
            energyCapacity: EnergyCapacity.validator,
            chargeCapacity: ChargeCapacity.validator,
            batteryTechnologies: BatteryTechnologies.validator.default([]),
            standards: z.array(z.string()).default([]),
            parallelableCapacity: ParallelableCapacity.validator,
        })
        .default({}),
    environmental: environmentalWithCoolingMethod,
});

export const BatteryValidator = ComponentValidator.merge(BatteryBase);
export const BatteryPayloadValidator = ComponentPayloadValidator.merge(BatteryBase);

export const BatteryDefinition: ComponentDefinition = {
    type: 'battery',
    name: 'Battery',
    plural: 'Batteries',
    indicator: 'BAT',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: true,
    ports: {
        min: 1,
        max: 1,
        powerFlowDirections: ['bidirectional'],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: BatteryValidator,
        payload: BatteryPayloadValidator,
    },
};

export type Battery = z.infer<typeof BatteryValidator>;
export type BatteryPayload = z.infer<typeof BatteryPayloadValidator>;
